/**
 * React Query hooks for onboarding authentication API endpoints
 * Following CLAUDE.md guidelines: Always use React Query for REST API calls
 */
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { SERVER_BASE_URL } from "../utils/helpers";

// API base URL for onboarding auth endpoints
const ONBOARDING_API_BASE = SERVER_BASE_URL || "http://localhost:8080";

// Query keys for cache management
export const onboardingAuthKeys = {
  all: ["onboarding-auth"] as const,
  expiration: (uuid: string) =>
    [...onboardingAuthKeys.all, "expiration", uuid] as const
};

/**
 * Response type for the expiration check endpoint
 */
export interface ExpirationCheckResponse {
  expired: boolean;
  message?: string;
}

/**
 * Hook to check if an onboarding UUID is expired.
 * This is a public endpoint that doesn't require authentication.
 *
 * @param uuid - The onboarding UUID to check
 * @param options - Optional configuration
 * @param options.enabled - Whether to run the query (default: true if uuid is provided)
 * @returns React Query result with expiration status
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCheckExpiration(uuid);
 *
 * if (isLoading) return <Spinner />;
 * if (data?.expired) return <ExpiredMessage message={data.message} />;
 * ```
 */
// Timeout duration for expiration check (10 seconds)
const EXPIRATION_CHECK_TIMEOUT_MS = 10000;

export function useCheckExpiration(
  uuid: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ExpirationCheckResponse>({
    queryKey: onboardingAuthKeys.expiration(uuid || ""),
    queryFn: async ({ signal }) => {
      if (!uuid) {
        return { expired: false };
      }

      // Create a timeout that will abort the request if it takes too long
      const timeoutId = setTimeout(() => {
        if (signal && !signal.aborted) {
          // AbortController from React Query's signal
          console.warn(
            `Expiration check timed out after ${EXPIRATION_CHECK_TIMEOUT_MS}ms`
          );
        }
      }, EXPIRATION_CHECK_TIMEOUT_MS);

      try {
        const response = await axios.get<ExpirationCheckResponse>(
          `${ONBOARDING_API_BASE}/onboarding-auth/check-expiration/${uuid}`,
          {
            signal, // Pass the abort signal to axios
            timeout: EXPIRATION_CHECK_TIMEOUT_MS // Also set axios timeout as backup
          }
        );
        return response.data;
      } catch (error) {
        // On timeout or network error, return non-expired to allow login attempt
        // The login flow itself will handle invalid/expired UUIDs
        if (axios.isAxiosError(error)) {
          if (error.code === "ECONNABORTED" || error.code === "ERR_CANCELED") {
            console.warn("Expiration check timed out, allowing login attempt");
            return { expired: false };
          }
          // For network errors (server down, etc.), allow login attempt
          if (error.code === "ERR_NETWORK" || !error.response) {
            console.warn(
              "Expiration check failed due to network error, allowing login attempt"
            );
            return { expired: false };
          }
        }
        // Re-throw other errors to be handled by React Query
        throw error;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    // Only run query if UUID is provided and enabled is not explicitly false
    enabled: !!uuid && options?.enabled !== false,
    // Cache expiration result for 5 minutes (user won't keep checking the same UUID)
    staleTime: 5 * 60 * 1000,
    // Don't retry on failure - if check fails, the login flow will handle it
    retry: false,
    // Don't refetch on window focus - expiration status won't change frequently
    refetchOnWindowFocus: false
  });
}
